import { OnboardingBuildSteps } from "@/features/onboarding/components/onboarding-build-steps";
import { OnboardingDarkBackground } from "@/features/onboarding/components/onboarding-dark-background";
import { OnboardingSmileLoader } from "@/features/onboarding/components/onboarding-smile-loader";
import { onboardingContentStyle } from "@/features/onboarding/constants/theme";
import { delay } from "@/features/user-profile/utils/delay";
import { useGetActivityLevel } from "@/hooks/requests/acivity/use-get-activity-level";
import { useUpdateActivityLevel } from "@/hooks/requests/acivity/use-update-activity-level";
import { useCreatePlan } from "@/hooks/requests/plan/use-create-plan";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { cn } from "@/utils/cn";
import { triggerHaptic } from "@/utils/haptics";
import { safeAreaPadding } from "@/utils/safe-area-classes";
import { IonContent, IonPage } from "@ionic/react";
import { useEffect, useRef, useState } from "react";

const STEPS = ["Goal", "Training", "Routine", "Targets"] as const;

const TICK_MS = 80;

/**
 * PUT /plans only writes an empty week/day/slot skeleton — meals are added later by
 * the chat agent — so it returns almost immediately. Without a floor this screen
 * would flash past before the loader could be read.
 */
const MIN_DWELL_MS = 2500;

/** How long to wait before silently re-attempting after a failed plan build. */
const RETRY_DELAY_MS = 2000;

const BuildingPage = () => {
  const { navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { activityLevel, isLoading: isActivityLoading } = useGetActivityLevel();
  const { updateActivityLevelAsync } = useUpdateActivityLevel();
  const { updatePlanAsync } = useCreatePlan();
  const { showError } = useToast();
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<"running" | "done" | "error">("running");
  const startedRef = useRef(false);
  const planDoneRef = useRef(false);
  const advancedRef = useRef(false);

  const run = async () => {
    setPhase("running");
    setProgress(0);
    planDoneRef.current = false;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (planDoneRef.current) {
          const next = Math.min(100, prev + 6);
          if (next >= 100) {
            clearInterval(interval);
            setPhase("done");
          }
          return next;
        }
        // Ease toward 90% while the plan request is in flight.
        return Math.min(90, prev + Math.max(0.4, (90 - prev) * 0.03));
      });
    }, TICK_MS);

    try {
      await Promise.all([
        preloadNextRoute(),
        delay(MIN_DWELL_MS),
        (async () => {
          // PUT /activity-level is the only call that computes calories and macros,
          // and it is not re-fired when the user goes back to change their weight,
          // goal, birthday or sex. Re-send the stored value so the targets — and the
          // plan built from them — reflect whatever they ended up with. The order
          // matters: /plans 404s if requirements have not been computed yet.
          const stored = activityLevel?.activityLevel;
          if (stored) {
            await updateActivityLevelAsync({ activityLevel: stored });
          }
          await updatePlanAsync();
        })(),
      ]);
      planDoneRef.current = true;
    } catch (error) {
      clearInterval(interval);
      setPhase("error");
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  useEffect(() => {
    if (startedRef.current) return;
    // Wait for the stored activity level to land, otherwise there is nothing to re-send.
    if (isActivityLoading) return;
    startedRef.current = true;
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActivityLoading]);

  // No CTA on this screen — advance automatically once the plan is ready.
  useEffect(() => {
    if (phase !== "done" || advancedRef.current) return;
    advancedRef.current = true;
    triggerHaptic();
    navigateToNextStep();
  }, [phase, navigateToNextStep]);

  // Button-less screen: surface a toast and silently retry after a short pause.
  useEffect(() => {
    if (phase !== "error") return;
    const id = setTimeout(() => void run(), RETRY_DELAY_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Fill Goal → Training → Routine as the plan builds; Targets stays open until
  // we hand off to the confirmation screen.
  const completedSteps = progress >= 88 ? 3 : progress >= 58 ? 2 : progress >= 28 ? 1 : 0;

  return (
    <IonPage>
      <IonContent fullscreen style={onboardingContentStyle}>
        <div className="relative h-full w-full overflow-hidden bg-[#130c2d]">
          <OnboardingDarkBackground />

          <div
            className={cn(
              "relative z-10 flex h-full flex-col",
              safeAreaPadding({ top: "16px", bottom: "12px" }),
            )}
          >
            <div className="px-9 pt-20 text-center">
              <h1 className="font-[family-name:var(--font-crimson-text)] text-[35px] leading-[45px] font-semibold tracking-[-1px] text-white">
                One moment.
              </h1>
              <p className="pt-3 text-base leading-[normal] font-medium text-white">
                I&rsquo;m working out the right place for us to start.
              </p>
            </div>

            <div className="flex flex-1 flex-col items-center justify-center gap-20 px-9">
              <OnboardingSmileLoader className="h-[41px] w-[56px]" />
              <OnboardingBuildSteps steps={STEPS} completed={completedSteps} />
            </div>

            <p className="px-9 pb-2 text-center text-sm font-medium text-[#9959ff]">Nearly ready</p>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default BuildingPage;
